/**
 * Week helpers for the weekly planning board. Dates are `YYYY-MM-DD` strings and all
 * arithmetic is done in UTC so a date never shifts across a timezone boundary.
 */

/** Monday through Saturday: the days the weekly board plans. */
export const WEEKLY_BOARD_DAY_COUNT = 6;

const DAY_MS = 24 * 60 * 60 * 1000;

function parseIsoDate(date: string): Date {
  return new Date(`${date}T00:00:00Z`);
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** A real calendar date in `YYYY-MM-DD` form (rejects `2026-13-45`). */
export function isIsoDate(date: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
  const parsed = parseIsoDate(date);
  return !Number.isNaN(parsed.getTime()) && toIsoDate(parsed) === date;
}

export function addDays(date: string, days: number): string {
  return toIsoDate(new Date(parseIsoDate(date).getTime() + days * DAY_MS));
}

/** The Monday of the week `date` falls in. */
export function getWeekStart(date: string): string {
  const weekday = parseIsoDate(date).getUTCDay(); // 0 = Sunday
  return addDays(date, -((weekday + 6) % 7));
}

export function isWeekStart(date: string): boolean {
  return parseIsoDate(date).getUTCDay() === 1;
}

/** The planned days (Mon–Sat) of the week starting on `weekStart`. */
export function getWeekDays(weekStart: string): string[] {
  return Array.from({ length: WEEKLY_BOARD_DAY_COUNT }, (_, i) => addDays(weekStart, i));
}

/** ISO 8601 week number and week-numbering year. */
export function getIsoWeek(date: string): { week: number; year: number } {
  const d = parseIsoDate(date);
  // The Thursday of this week decides the year the week belongs to.
  const thursday = new Date(d.getTime() + (3 - ((d.getUTCDay() + 6) % 7)) * DAY_MS);
  const yearStart = Date.UTC(thursday.getUTCFullYear(), 0, 1);
  return {
    week: Math.floor((thursday.getTime() - yearStart) / DAY_MS / 7) + 1,
    year: thursday.getUTCFullYear(),
  };
}
